import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

import type { TripDetailsResponse, TripTrackerResponse } from '@/src/types/tripDetails'
import type { HotelTripDetailsResponse } from '@/src/types/hotelTripDetails'
import { isFlyin } from '@/src/utils/generalUtils/isFlyin'
import setCommonHeaders from '@/src/utils/rtkQuery/setCommonHeaders'
import {
    buildFlyinQuerySuffix,
    getFetchControlHeader,
    getLanHeader,
    getTripApiBase
} from '@/src/utils/rtkQuery/tripApiBase'

interface TripDetailsQueryParams {
    tripId: string
    chnl?: string
    recordType?: string
    account?: string
    useV2?: boolean
    locale?: string
}

interface TripFaqQueryParams {
    tripId: string
    chnl?: string
    recordType?: string
    account?: string
    locale?: string
}

interface TripCaseDetailsQueryParams {
    tripId: string
    chnl?: string
    recordType?: string
    account?: string
}

interface RefundStatusQueryParams {
    tripId: string
    chnl?: string
    account?: string
    fetchHtml?: boolean
}

interface SendCommunicationRequest {
    channel: string[]
    mobileNumber: string
    tripRef: string
    communicationEvent: string
    sourceType: string
    account?: string
    chnl?: string
}

export const tripDetailsApi = createApi({
    reducerPath: 'tripDetailsApi',
    baseQuery: fetchBaseQuery({
        baseUrl: '',
        prepareHeaders: headers => {
            return setCommonHeaders(headers)
        }
    }),
    tagTypes: ['TripDetails'],
    keepUnusedDataFor: 600,
    endpoints: builder => ({
        getTripDetails: builder.query<TripDetailsResponse, TripDetailsQueryParams>({
            query: ({ tripId, chnl, recordType, account, useV2, locale }) => {
                const apiBase = getTripApiBase(chnl)
                const basePath = useV2 ? `${apiBase}/v2/tripDetails/` : `${apiBase}/tripDetails/`
                const suffix = buildFlyinQuerySuffix(chnl, recordType)
                return {
                    url: `${basePath}${tripId}${suffix}`,
                    headers: {
                        ...(getFetchControlHeader({ chnl, account }) || {}),
                        ...getLanHeader(locale)
                    }
                }
            },
            providesTags: (result, error, { tripId }) => [{ type: 'TripDetails', id: `details-${tripId}` }]
        }),

        getHotelTripDetails: builder.query<
            HotelTripDetailsResponse,
            { tripId: string; chnl?: string; locale?: string }
        >({
            // This node endpoint localizes via the `language` query param (ar/en) only
            query: ({ tripId, chnl, locale }) => {
                const language = locale?.toLowerCase().startsWith('ar') ? 'ar' : 'en'
                return {
                    url: `/node/flight/trip/details/${tripId}?language=${language}`,
                    headers: chnl ? { chnl } : {}
                }
            },
            providesTags: (result, error, { tripId }) => [{ type: 'TripDetails', id: `hotel-details-${tripId}` }]
        }),

        getTripFaq: builder.query<any, TripFaqQueryParams>({
            query: ({ tripId, chnl, recordType, account, locale }) => {
                const basePath = `${getTripApiBase(chnl)}/tripFAQs/`
                const suffix = buildFlyinQuerySuffix(chnl, recordType)
                return {
                    url: `${basePath}${tripId}${suffix}`,
                    headers: {
                        ...(getFetchControlHeader({ chnl, account }) || {}),
                        ...getLanHeader(locale)
                    }
                }
            },
            providesTags: (result, error, { tripId }) => [{ type: 'TripDetails', id: `faq-${tripId}` }]
        }),

        getTripCaseDetails: builder.query<any, TripCaseDetailsQueryParams>({
            query: ({ tripId, chnl, recordType, account }) => {
                const basePath = '/communication/selfcare/api/casesList/'
                const suffix = buildFlyinQuerySuffix(chnl, recordType)
                return {
                    url: `${basePath}${tripId}${suffix}`,
                    headers: getFetchControlHeader({ chnl, account })
                }
            },
            providesTags: (result, error, { tripId }) => [{ type: 'TripDetails', id: `cases-${tripId}` }]
        }),

        getTripTrackStatus: builder.query<TripTrackerResponse, { tripId: string; chnl?: string; account?: string }>({
            query: ({ tripId, chnl, account }) => ({
                url: `${getTripApiBase(chnl)}/trip-track-status/${tripId}`,
                headers: getFetchControlHeader({ chnl, account })
            }),
            providesTags: (result, error, { tripId }) => [{ type: 'TripDetails', id: `tracker-${tripId}` }]
        }),

        getRefundStatus: builder.query<any, RefundStatusQueryParams>({
            query: ({ tripId, fetchHtml, chnl, account }) => {
                const basePath = `${getTripApiBase(chnl)}/v2/refundStatus/`
                const suffix = fetchHtml ? '?fetchHtml=true' : ''
                return {
                    url: `${basePath}${tripId}${suffix}`,
                    headers: getFetchControlHeader({ chnl, account })
                }
            },
            providesTags: (result, error, { tripId }) => [{ type: 'TripDetails', id: `refund-${tripId}` }]
        }),

        downloadEticket: builder.query<any, { tripId: string; chnl?: string; account?: string }>({
            query: ({ tripId, chnl, account }) => ({
                url: `${getTripApiBase(chnl)}/downloadETicket/${tripId}`,
                headers: getFetchControlHeader({ chnl, account }),
                responseHandler: 'text'
            }),
            providesTags: (result, error, { tripId }) => [{ type: 'TripDetails', id: `eticket-${tripId}` }]
        }),

        downloadInvoice: builder.query<any, { tripId: string; chnl?: string; account?: string }>({
            query: ({ tripId, chnl, account }) => ({
                url: `${getTripApiBase(chnl)}/downloadInvoice/${tripId}`,
                headers: getFetchControlHeader({ chnl, account }),
                responseHandler: 'text'
            }),
            providesTags: (result, error, { tripId }) => [{ type: 'TripDetails', id: `invoice-${tripId}` }]
        }),

        authenticateContent: builder.query<any, { chnl?: string; account?: string } | void>({
            query: input => ({
                url: `${getTripApiBase((input as { chnl?: string; account?: string } | undefined)?.chnl)}/authenticate-content`,
                headers: getFetchControlHeader({
                    chnl: (input as { chnl?: string; account?: string } | undefined)?.chnl,
                    account: (input as { chnl?: string; account?: string } | undefined)?.account
                })
            })
        }),

        sendInvoice: builder.query<any, { tripId: string; email: string; chnl?: string; account?: string }>({
            query: ({ tripId, email, chnl, account }) => ({
                url: `/communication/selfcare/api/sendInvoice/${tripId}?toEmail=${encodeURIComponent(email)}`,
                headers: getFetchControlHeader({ chnl, account })
            })
        }),

        sendEticket: builder.query<any, { tripId: string; email: string; chnl?: string; account?: string }>({
            query: ({ tripId, email, chnl, account }) => ({
                url: `/communication/selfcare/api/v2/sendEticket/${tripId}?toEmail=${encodeURIComponent(email)}`,
                headers: getFetchControlHeader({ chnl, account })
            })
        }),

        sendCommunication: builder.mutation<any, SendCommunicationRequest>({
            query: ({ channel, mobileNumber, tripRef, communicationEvent, sourceType, account, chnl }) => ({
                url: '/communication/sendCommunication',
                method: 'POST',
                body: {
                    channel,
                    mobileNumber,
                    tripRef,
                    communicationEvent,
                    sourceType
                },
                headers: getFetchControlHeader({ chnl, account })
            })
        })
    })
})

export const {
    useGetTripDetailsQuery,
    useGetHotelTripDetailsQuery,
    useGetTripFaqQuery,
    useGetTripTrackStatusQuery,
    useGetRefundStatusQuery,
    useLazyGetTripTrackStatusQuery,
    useLazyGetRefundStatusQuery,
    useLazyDownloadEticketQuery,
    useLazyDownloadInvoiceQuery,
    useLazyAuthenticateContentQuery,
    useLazySendInvoiceQuery,
    useLazySendEticketQuery,
    useSendCommunicationMutation
} = tripDetailsApi

export default tripDetailsApi
